/**
 * Enhanced Product Card Component
 * For Shopify Plus theme - all data handled server-side via Liquid
 * OrderGroove and Yotpo integrate via their Shopify apps
 */

class ProductCardEnhanced extends HTMLElement {
  constructor() {
    super();

    this.productId = this.dataset.productId;

    // All product data is already in the DOM from Liquid
    // No need for API fetching - this is a Shopify Plus theme

    this.init();
  }

  /**
   * Helper: Generate srcset for responsive images
   * @param {string} baseUrl - Base image URL without query params
   * @returns {string} Complete srcset string
   */
  _generateSrcset(baseUrl) {
    const base = baseUrl.split('?')[0];
    return `${base}?width=200 200w, ${base}?width=300 300w, ${base}?width=400 400w, ${base}?width=500 500w, ${base}?width=600 600w, ${base}?width=700 700w, ${base}?width=800 800w, ${base}?width=1000 1000w`;
  }

  /**
   * Helper: Normalize URL for comparison
   * @param {string} url - URL to normalize
   * @returns {string} Normalized URL
   */
  _normalizeUrl(url) {
    if (!url) return '';
    return url.split('?')[0].replace(/^https?:\/\//, '');
  }

  init() {
    // Setup UI interactions only
    // Custom purchase options with OrderGroove styling
    // Yotpo app handles review display automatically
    // All data is server-rendered via Liquid

    this.setupImageCarousel();
    this.setupPurchaseOptionToggles();
    this.setupFrequencySelector();
    this.setupBuyNow();
    // Initialize stock status BEFORE setting up variant selectors
    // This ensures primary/secondary positions are detected first
    this.initializeStockStatus();
    this.setupVariantSelectors();
    this.setupColorSwatches();
  }

  /**
   * Initialize variant filtering
   * Stores all original options and sets up dynamic filtering
   */
  initializeStockStatus() {
    const variantJsonScript = this.querySelector('[data-variant-json]');
    if (!variantJsonScript) return;

    const variants = JSON.parse(variantJsonScript.textContent);
    if (!variants || variants.length === 0) return;

    // Store variants for later use
    this.variants = variants;

    // Determine which options are primary (Flavor/Color) vs secondary (Size/Serving)
    const variantSelects = this.querySelectorAll('.product-card-enhanced__variant-select');
    this.primarySelectPosition = null;
    this.secondarySelectPositions = [];

    variantSelects.forEach(select => {
      const optionPosition = parseInt(select.dataset.optionPosition);
      const optionName = (select.dataset.optionName || '').toLowerCase();

      // Determine if this is primary (Flavor, Color, Colour, Flavour) or secondary (Size, Serving)
      const isPrimary = optionName.includes('flavor') ||
                       optionName.includes('flavour') ||
                       optionName.includes('color') ||
                       optionName.includes('colour');

      const isSecondary = optionName.includes('size') ||
                         optionName.includes('serving');

      if (isPrimary) {
        this.primarySelectPosition = optionPosition;
      } else if (isSecondary) {
        this.secondarySelectPositions.push(optionPosition);
      }
    });

    // Fallback: if we couldn't detect by name, assume position 1 is primary
    if (this.primarySelectPosition === null) {
      this.primarySelectPosition = 1;
      variantSelects.forEach(select => {
        const optionPosition = parseInt(select.dataset.optionPosition);
        if (optionPosition !== 1) {
          this.secondarySelectPositions.push(optionPosition);
        }
      });
    }


    // Store original options for all selects so we can restore them
    this.originalOptions = {};

    variantSelects.forEach(select => {
      const optionPosition = parseInt(select.dataset.optionPosition);

      // Store all original options
      this.originalOptions[optionPosition] = Array.from(select.options).map(option => ({
        value: option.value,
        text: option.text,
        selected: option.selected
      }));
    });

    // Filter secondary options based on initial primary selection
    this.filterSecondaryOptions();

    // Check if primary options should show out of stock
    this.updatePrimaryOptionLabels();

    // Mark OOS flavor swatches
    this.updateFlavorSwatchStockStatus();
  }

  /**
   * Mark flavor swatch radio inputs as disabled when ALL variants for that flavor are OOS.
   */
  updateFlavorSwatchStockStatus() {
    if (!this.variants) return;

    const flavorSwatches = this.querySelector('.product-card-enhanced__flavor-swatches');
    if (!flavorSwatches) return;

    const optionPosition = parseInt(flavorSwatches.dataset.optionPosition);
    const optionIndex = optionPosition - 1;
    const swatchInputs = flavorSwatches.querySelectorAll('[type="radio"]');

    swatchInputs.forEach(input => {
      const flavorValue = input.value;
      const hasAvailable = this.variants.some(v =>
        v.options[optionIndex] === flavorValue && v.available
      );

      if (!hasAvailable) {
        input.disabled = true;
        const label = input.nextElementSibling;
        if (label) label.classList.add('is-disabled');
      }
    });
  }

  /**
   * Filter secondary option dropdowns to only show available options
   * for the currently selected primary option (flavor/color)
   */
  filterSecondaryOptions() {
    if (!this.variants || !this.originalOptions || this.primarySelectPosition === null) return;

    // Get primary option selection (flavor/color) - query specifically for SELECT elements
    const primarySelect = this.querySelector(`select.product-card-enhanced__variant-select[data-option-position="${this.primarySelectPosition}"]`);
    if (!primarySelect) return;

    const primaryValue = primarySelect.value;
    const primaryOptionIndex = this.primarySelectPosition - 1; // Convert to 0-based index

    // Get all variant selects
    const variantSelects = this.querySelectorAll('.product-card-enhanced__variant-select');

    variantSelects.forEach(select => {
      const optionPosition = parseInt(select.dataset.optionPosition);

      // Skip primary option - NEVER filter it
      if (optionPosition === this.primarySelectPosition) return;

      // Only filter secondary options (Size/Serving)
      if (!this.secondarySelectPositions.includes(optionPosition)) return;

      // Get currently selected value before filtering
      const currentlySelected = select.value;
      const secondaryOptionIndex = optionPosition - 1; // Convert to 0-based index

      // Find all option values that EXIST for this primary selection (regardless of stock)
      const existingValues = new Set();
      const availableValues = new Set();

      this.variants.forEach(variant => {
        const variantPrimaryValue = variant.options[primaryOptionIndex];
        const variantSecondaryValue = variant.options[secondaryOptionIndex];

        // If this variant matches the selected primary option
        if (variantPrimaryValue === primaryValue) {
          // Track that this size exists for this flavor
          existingValues.add(variantSecondaryValue);

          // Track if it's also available
          if (variant.available) {
            availableValues.add(variantSecondaryValue);
          }
        }
      });

      // Clear the select
      select.innerHTML = '';

      // Re-populate with only options that EXIST for this flavor, marking unavailable ones as disabled
      const originalOptions = this.originalOptions[optionPosition] || [];
      let hasSelectedOption = false;
      let firstAvailableOption = null;

      originalOptions.forEach(optionData => {
        // Only show this option if it exists for the selected flavor
        if (!existingValues.has(optionData.value)) {
          return; // Skip this option - it doesn't exist for this flavor
        }

        const option = document.createElement('option');
        const isAvailable = availableValues.has(optionData.value);

        option.value = optionData.value;

        if (isAvailable) {
          // Available option - normal styling
          option.text = optionData.value;
          option.disabled = false;
          option.style.color = '';
          option.style.fontStyle = '';

          // Track first available option for auto-selection
          if (firstAvailableOption === null) {
            firstAvailableOption = option;
          }

          // Restore selection if this was previously selected and still available
          if (optionData.value === currentlySelected) {
            option.selected = true;
            hasSelectedOption = true;
          }
        } else {
          // Out of stock option - greyed out and disabled
          option.text = `${optionData.value} (Out of Stock)`;
          option.disabled = true;
          option.style.color = '#6b7280'; // Gray text
          option.style.fontStyle = 'italic';
        }

        select.appendChild(option);
      });

      // Auto-select first available option if current selection is no longer available
      if (!hasSelectedOption && firstAvailableOption) {
        firstAvailableOption.selected = true;
      }
    });
  }

  /**
   * Update primary option labels to show "(Out of Stock)" only if ALL
   * secondary options are unavailable for that primary option
   */
  updatePrimaryOptionLabels() {
    if (!this.variants || !this.originalOptions || this.primarySelectPosition === null) return;

    // Query specifically for SELECT elements to avoid matching fieldsets
    const primarySelect = this.querySelector(`select.product-card-enhanced__variant-select[data-option-position="${this.primarySelectPosition}"]`);
    if (!primarySelect) return;

    // Check if primarySelect has options property and it's iterable
    if (!primarySelect.options || primarySelect.options.length === 0) return;

    const primaryOptionIndex = this.primarySelectPosition - 1; // Convert to 0-based index

    Array.from(primarySelect.options).forEach(option => {
      const primaryValue = option.value;

      // Check if this primary option has ANY available variants
      const hasAnyAvailable = this.variants.some(variant => {
        const variantPrimaryValue = variant.options[primaryOptionIndex];
        return variantPrimaryValue === primaryValue && variant.available;
      });

      // Clean the original value
      const originalOption = this.originalOptions[this.primarySelectPosition]?.find(o => o.value === primaryValue);
      const cleanText = originalOption ? originalOption.text.replace(/\s*\(Out of Stock\)$/, '') : primaryValue;

      if (!hasAnyAvailable) {
        // All variants for this flavor are out of stock
        // But keep it selectable - users can still choose it and see no sizes available
        option.text = `${cleanText} (Out of Stock)`;
        option.disabled = false; // Keep selectable
        option.style.color = '#6b7280'; // Gray text to indicate unavailability
        option.style.fontStyle = 'italic'; // Italicize to show it's different
      } else {
        // At least one variant is available
        option.text = cleanText;
        option.disabled = false;
        option.style.color = '';
        option.style.fontStyle = '';
      }
    });
  }

  /**
   * Check if current variant selection is out of stock
   * @param {Object} matchedVariant - The currently selected variant
   * @returns {boolean} - True if variant is out of stock
   */
  isCurrentSelectionOutOfStock(matchedVariant) {
    // First check if any secondary dropdown shows "No sizes available"
    if (this.secondarySelectPositions && this.secondarySelectPositions.length > 0) {
      const hasNoSizesAvailable = this.secondarySelectPositions.some(position => {
        const select = this.querySelector(`[data-option-position="${position}"]`);
        return select && select.value === '';
      });

      if (hasNoSizesAvailable) return true; // Out of stock
    }

    // Otherwise check the matched variant
    return matchedVariant ? !matchedVariant.available : true;
  }

  /**
   * Setup image carousel with navigation and touch gestures
   */
  setupImageCarousel() {
    const carousel = this.querySelector('[data-image-carousel]');
    if (!carousel) return;

    const track = carousel.querySelector('[data-carousel-track]');
    const slides = carousel.querySelectorAll('[data-carousel-slide]');
    const prevButton = carousel.querySelector('[data-carousel-prev]');
    const nextButton = carousel.querySelector('[data-carousel-next]');
    const dots = carousel.querySelectorAll('[data-carousel-dot]');
    const imageCountInput = carousel.querySelector('[data-image-count]');

    if (!track || slides.length === 0) return;

    const imageCount = parseInt(imageCountInput?.value || slides.length);
    let currentIndex = 0;
    let touchStartX = 0;
    let touchEndX = 0;

    // Update carousel position
    const updateCarousel = () => {
      track.style.transform = `translateX(-${currentIndex * 100}%)`;

      // Update dots
      dots.forEach((dot, index) => {
        if (index === currentIndex) {
          dot.classList.add('active');
        } else {
          dot.classList.remove('active');
        }
      });

      // Update arrow states
      if (prevButton) {
        prevButton.disabled = currentIndex === 0;
      }
      if (nextButton) {
        nextButton.disabled = currentIndex === imageCount - 1;
      }
    };

    // Navigate to specific index
    const goToSlide = (index) => {
      if (index < 0 || index >= imageCount) return;
      currentIndex = index;
      updateCarousel();
    };

    // Previous slide
    const goToPrev = () => {
      if (currentIndex > 0) {
        goToSlide(currentIndex - 1);
      }
    };

    // Next slide
    const goToNext = () => {
      if (currentIndex < imageCount - 1) {
        goToSlide(currentIndex + 1);
      }
    };

    // Arrow button listeners
    if (prevButton) {
      prevButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        goToPrev();
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        goToNext();
      });
    }

    // Dot indicators
    dots.forEach((dot, index) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        goToSlide(index);
      });
    });

    // Touch gestures for mobile
    track.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    track.addEventListener('touchmove', (e) => {
      touchEndX = e.changedTouches[0].screenX;
    }, { passive: true });

    track.addEventListener('touchend', () => {
      const swipeThreshold = 50; // Minimum swipe distance in pixels
      const diff = touchStartX - touchEndX;

      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          // Swipe left - next image
          goToNext();
        } else {
          // Swipe right - previous image
          goToPrev();
        }
      }

      touchStartX = 0;
      touchEndX = 0;
    });

    // Keyboard navigation
    carousel.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNext();
      }
    });

    // Initialize
    updateCarousel();
  }

  /**
   * Setup purchase option radio button visual states
   * Handles subscription vs one-time purchase selection
   */
  setupPurchaseOptionToggles() {
    const purchaseOptions = this.querySelectorAll('.og-radio');

    purchaseOptions.forEach(radio => {
      this.setupPurchaseOptionToggle(radio);
    });

    // Initialize form with selling_plan if subscribe is checked by default
    const subscribeRadio = this.querySelector('.og-radio[value="subscribe"]');
    const form = this.querySelector('.product-card-enhanced__form');
    const addToCartButton = this.querySelector('.product-card-enhanced__add-to-cart');

    if (subscribeRadio && subscribeRadio.checked && form) {
      const sellingPlanId = subscribeRadio.dataset.sellingPlanId;
      if (sellingPlanId) {
        const planInput = document.createElement('input');
        planInput.type = 'hidden';
        planInput.name = 'selling_plan';
        planInput.value = sellingPlanId;
        form.appendChild(planInput);
      }

      // Set button text to subscription text if subscribe is checked by default
      if (addToCartButton) {
        const buttonTextSpan = addToCartButton.querySelector('span');
        if (buttonTextSpan) {
          buttonTextSpan.textContent = 'ADD SUBSCRIPTION';
        }
      }
    }

    this.syncPurchaseOptionSelectionState();
  }

  /**
   * Sync visual selected state for purchase options.
   * Uses explicit classes so frequency visibility does not rely on :has() support.
   */
  syncPurchaseOptionSelectionState() {
    const purchaseOptions = this.querySelectorAll('.og-purchase-options .og-option');

    purchaseOptions.forEach((option) => {
      const radio = option.querySelector('.og-radio');
      option.classList.toggle('og-option--selected', !!radio?.checked);
    });
  }

  /**
   * Setup frequency selector dropdown
   */
  setupFrequencySelector() {
    const frequencySelect = this.querySelector('[data-frequency-select]');
    if (!frequencySelect) return;

    frequencySelect.addEventListener('change', (e) => {
      const selectedPlanId = e.target.value;

      // Update the selling plan ID in the subscribe radio button
      const subscribeRadio = this.querySelector('.og-radio[value="subscribe"]');
      if (subscribeRadio) {
        subscribeRadio.setAttribute('data-selling-plan-id', selectedPlanId);

        // If subscribe is currently selected, update the form
        if (subscribeRadio.checked) {
          const form = this.querySelector('.product-card-enhanced__form');
          const existingPlanInput = form?.querySelector('input[name="selling_plan"]');
          if (existingPlanInput) {
            existingPlanInput.value = selectedPlanId;
          }
        }
      }

    });
  }

  /**
   * Setup Buy Now button to go directly to Shopify checkout
   * Adds item to cart and redirects to checkout
   */
  setupBuyNow() {
    const buyNowButton = this.querySelector('[data-buy-now]');

    if (!buyNowButton) return;

    buyNowButton.addEventListener('click', async (e) => {
      e.preventDefault();

      // Get the currently selected variant ID
      const variantInput = this.querySelector('.product-card-enhanced__selected-variant');
      const variantId = variantInput ? variantInput.value : buyNowButton.dataset.variantId;

      if (!variantId) {
        console.error('No variant ID found for buy now button');
        return;
      }

      // Check if subscription is selected
      const subscribeRadio = this.querySelector('.og-radio[value="subscribe"]');
      const isSubscribeSelected = subscribeRadio && subscribeRadio.checked;
      let sellingPlanId = null;

      if (isSubscribeSelected) {
        sellingPlanId = subscribeRadio.dataset.sellingPlanId;
      }

      // Disable button and show loading state
      buyNowButton.disabled = true;
      const originalText = buyNowButton.innerHTML;
      buyNowButton.innerHTML = '<span>LOADING...</span>';

      try {
        // Build cart item object
        const cartItem = {
          id: variantId,
          quantity: 1
        };

        // Add selling_plan if subscription is selected
        if (sellingPlanId) {
          cartItem.selling_plan = sellingPlanId;
        }

        // Add item to cart using Shopify Cart API
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [cartItem]
          })
        });

        if (!response.ok) {
          throw new Error('Failed to add item to cart');
        }

        // Redirect to checkout
        window.location.href = '/checkout';
      } catch (error) {
        console.error('Error adding to cart:', error);

        // Reset button state
        buyNowButton.disabled = false;
        buyNowButton.innerHTML = originalText;

        // Show error message to user
        alert('Sorry, there was an error processing your request. Please try again.');
      }
    });
  }

  /**
   * Setup variant selector dropdowns
   * Handles flavor, size, and other option changes
   * Updates price and selected variant
   */
  setupVariantSelectors() {
    // Get all variant selects
    const variantSelects = this.querySelectorAll('.product-card-enhanced__variant-select');

    if (variantSelects.length === 0) return;

    // Get variant JSON data
    const variantJsonScript = this.querySelector('[data-variant-json]');
    if (!variantJsonScript) return;

    const variants = JSON.parse(variantJsonScript.textContent);

    // Add change listeners to all selects
    variantSelects.forEach(select => {
      select.addEventListener('change', () => {
        const optionPosition = parseInt(select.dataset.optionPosition);

        // If primary option (Flavor/Color) changes, filter secondary options
        if (this.primarySelectPosition && optionPosition === this.primarySelectPosition) {
          this.filterSecondaryOptions();
        }

        this.updateSelectedVariant(variants, variantSelects);
      });
    });

    // Initialize with first variant
    this.updateSelectedVariant(variants, variantSelects);
  }

  /**
   * Update the selected variant based on option selections
   * @param {Array} variants - All product variants
   * @param {NodeList} selects - All variant select elements
   */
  updateSelectedVariant(variants, selects) {
    if (!variants || variants.length === 0) {
      console.error('No variants available');
      return;
    }

    // Build array of selected values in order of option position
    const selectsArray = Array.from(selects);

    // Sort by option position to ensure correct order
    selectsArray.sort((a, b) => {
      const posA = parseInt(a.dataset.optionPosition) || 0;
      const posB = parseInt(b.dataset.optionPosition) || 0;
      return posA - posB;
    });

    // Get selected values in correct order
    const selectedOptions = selectsArray.map(select => select.value);

    // Check if any option has an empty value (e.g., "No sizes available")
    const hasEmptyOption = selectedOptions.some(value => value === '');

    if (hasEmptyOption) {
      // Can't match a variant with empty options - but still update image for primary option
      const primaryOptionIndex = this.primarySelectPosition - 1;
      const primaryValue = selectedOptions[primaryOptionIndex];

      const partialMatchVariant = variants.find(variant => {
        return variant.options[primaryOptionIndex] === primaryValue;
      });

      if (partialMatchVariant) {
        this.updateVariantImage(partialMatchVariant);
      }

      this.showOutOfStock();
      return;
    }

    // Find matching variant - compare all options in order
    const matchedVariant = variants.find(variant => {
      // Variant options is an array like ["Flavor Value", "Size Value"]
      // selectedOptions is also an array in the same order
      if (!variant.options || variant.options.length !== selectedOptions.length) {
        return false;
      }

      // All options must match
      return selectedOptions.every((selectedValue, index) => {
        return variant.options[index] === selectedValue;
      });
    });

    if (!matchedVariant) {
      console.error('❌ No matching variant found for options:', selectedOptions);
      console.error('Available variants:', variants.map(v => ({ id: v.id, options: v.options })));
      return;
    }

    // Update hidden variant ID field
    const variantInput = this.querySelector('.product-card-enhanced__selected-variant');
    if (variantInput) {
      variantInput.value = matchedVariant.id;
      variantInput.dataset.selectedVariantId = matchedVariant.id;
    }

    // Update price display
    this.updatePrice(matchedVariant);

    // Update add to cart button variant ID
    const addToCartForm = this.querySelector('.product-card-enhanced__form');
    if (addToCartForm) {
      const variantIdInput = addToCartForm.querySelector('input[name="id"]');
      if (variantIdInput) {
        variantIdInput.value = matchedVariant.id;
      }
    }

    // Update buy now button
    const buyNowButton = this.querySelector('[data-buy-now]');
    if (buyNowButton) {
      buyNowButton.dataset.variantId = matchedVariant.id;
    }

    // Update product image if variant has featured media
    this.updateVariantImage(matchedVariant);

    // Update OrderGroove widget with new variant
    this.updateOrderGrooveVariant(matchedVariant);

    // Check if current selection is out of stock
    const isOutOfStock = this.isCurrentSelectionOutOfStock(matchedVariant);

    if (isOutOfStock) {
      this.showOutOfStock();
    } else {
      this.hideOutOfStock();
    }
  }

  /**
   * Update Subscribe & Save visibility based on variant
   * Checks OrderGroove selling plan allocations to determine if variant supports subscriptions
   * @param {Object} variant - Selected variant
   */
  updateSubscribeVisibility(variant) {
    const subscribeOption = this.querySelector('[data-purchase-type="subscribe"]');
    if (!subscribeOption) return; // No subscribe option in template

    // Check if this specific variant has selling plans from OrderGroove
    // OrderGroove controls which variants support subscriptions via selling_plan_allocations
    const hasSellingPlans = variant.has_selling_plans === true;

    if (!hasSellingPlans) {
      // This variant doesn't support subscriptions in OrderGroove
      // Hide subscribe option and select one-time
      subscribeOption.style.display = 'none';
      const oneTimeRadio = this.querySelector('[data-purchase-type="one-time"] input[type="radio"]');
      if (oneTimeRadio) {
        oneTimeRadio.checked = true;
      }
    } else {
      // This variant supports subscriptions
      subscribeOption.style.display = '';
    }
  }

  /**
   * Update product image when variant changes
   * @param {Object} variant - Selected variant
   */
  updateVariantImage(matchedVariant) {
    // Check if variant has featured media/image
    if (!matchedVariant.featured_media || !matchedVariant.featured_media.src) return;

    // Get the first carousel slide (main product image)
    const firstSlide = this.querySelector('[data-carousel-slide]');
    if (!firstSlide) return;

    const imageElement = firstSlide.querySelector('.product-card-enhanced__image');
    if (!imageElement) return;

    // Update the image source to the variant's featured image
    const newSrc = matchedVariant.featured_media.src;

    const currentNormalizedSrc = this._normalizeUrl(imageElement.src);
    const newNormalizedSrc = this._normalizeUrl(newSrc);

    // Only update if the src is different to avoid unnecessary reloads
    if (currentNormalizedSrc !== newNormalizedSrc) {
      // Add smooth transition
      imageElement.style.opacity = '0.5';
      imageElement.src = newSrc;

      // Update srcset for responsive images
      imageElement.srcset = this._generateSrcset(newSrc);

      // Update alt text if available
      if (matchedVariant.featured_media.alt) {
        imageElement.alt = matchedVariant.featured_media.alt;
      }

      // Restore opacity once loaded
      imageElement.onload = () => {
        imageElement.style.opacity = '1';
      };

      imageElement.onerror = () => {
        imageElement.style.opacity = '1'; // Restore opacity even on error
      };
    }

    // Reset carousel to first image to show the new variant image
    const carousel = this.querySelector('[data-image-carousel]');
    if (carousel) {
      const track = carousel.querySelector('[data-carousel-track]');
      const dots = carousel.querySelectorAll('[data-carousel-dot]');
      const prevButton = carousel.querySelector('[data-carousel-prev]');
      const nextButton = carousel.querySelector('[data-carousel-next]');

      // Reset to first slide
      if (track) {
        track.style.transform = 'translateX(0%)';
      }

      // Update dots
      dots.forEach((dot, index) => {
        if (index === 0) {
          dot.classList.add('active');
        } else {
          dot.classList.remove('active');
        }
      });

      // Update arrow states
      if (prevButton) {
        prevButton.disabled = true;
      }
      if (nextButton) {
        const imageCountInput = carousel.querySelector('[data-image-count]');
        const imageCount = parseInt(imageCountInput?.value || 1);
        nextButton.disabled = imageCount <= 1;
      }
    }
  }

  /**
   * Update price display
   * @param {Object} variant - Selected variant
   */
  updatePrice(variant) {
    const priceElement = this.querySelector('.product-card-enhanced__price');
    const comparePriceElement = this.querySelector('.product-card-enhanced__compare-price');
    const savingsElement = this.querySelector('.product-card-enhanced__savings');

    if (priceElement) {
      // Format price (Shopify provides price in cents)
      const price = (variant.price / 100).toFixed(2);
      priceElement.textContent = `$${price}`;
    }

    // Handle compare at price (sale pricing)
    if (comparePriceElement && variant.compare_at_price) {
      const comparePrice = (variant.compare_at_price / 100).toFixed(2);
      comparePriceElement.textContent = `$${comparePrice}`;
      comparePriceElement.style.display = '';

      // Calculate savings
      if (savingsElement) {
        const saveAmount = ((variant.compare_at_price - variant.price) / 100).toFixed(2);
        const savePercentage = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);

        const saveBadge = savingsElement.querySelector('.product-card-enhanced__save-badge');
        const saveAmountSpan = savingsElement.querySelector('.product-card-enhanced__save-amount');

        if (saveBadge) saveBadge.textContent = `Save ${savePercentage}%`;
        if (saveAmountSpan) saveAmountSpan.textContent = `You save $${saveAmount}`;

        savingsElement.style.display = '';
      }
    } else {
      if (comparePriceElement) comparePriceElement.style.display = 'none';
      if (savingsElement) savingsElement.style.display = 'none';
    }

    // OrderGroove widget handles subscription pricing automatically
    // updateSubscriptionPrice() removed - no longer needed with native widget
  }

  /**
   * Update custom purchase options with new variant pricing
   * @param {Object} variant - Selected variant
   */
  updateOrderGrooveVariant(variant) {
    // Update one-time price
    const onetimePriceElement = this.querySelector('[data-price-display="onetime"]');
    if (onetimePriceElement) {
      const price = (variant.price / 100).toFixed(2);
      onetimePriceElement.textContent = `$${price}`;
    }

    // Get or create subscription option
    const subscribeOption = this.querySelector('.og-option--subscribe');

    if (variant.has_selling_plans && variant.selling_plan) {
      // Variant has subscription - show/update the option
      if (!subscribeOption) {
        // Create subscription option if it doesn't exist
        this.createSubscriptionOption(variant);
      } else {
        // Update existing subscription option
        const subscriptionPriceElement = subscribeOption.querySelector('[data-price-display="subscription"]');
        const subscribeRadio = subscribeOption.querySelector('.og-radio[value="subscribe"]');
        const badge = subscribeOption.querySelector('.og-badge');
        const benefitText = subscribeOption.querySelector('.og-benefit span');
        const frequencySelect = subscribeOption.querySelector('[data-frequency-select]');

        if (subscriptionPriceElement) {
          const subPrice = (variant.selling_plan.subscription_price / 100).toFixed(2);
          subscriptionPriceElement.textContent = `$${subPrice}`;
          subscriptionPriceElement.setAttribute('data-base-subscription-price', variant.selling_plan.subscription_price);
        }

        if (subscribeRadio) {
          subscribeRadio.setAttribute('data-selling-plan-id', variant.selling_plan.id);
        }

        if (badge) {
          badge.textContent = `${variant.selling_plan.discount_percentage}% OFF + FREE SHIPPING`;
        }

        if (benefitText) {
          benefitText.textContent = `Get ${variant.selling_plan.discount_percentage}% off every order`;
        }

        // Update frequency dropdown with new variant's selling plan options
        if (frequencySelect && variant.selling_plan_options) {
          frequencySelect.innerHTML = '';
          variant.selling_plan_options.forEach((plan, index) => {
            const option = document.createElement('option');
            option.value = plan.id;
            // Use clean name from variant data (e.g., "4 weeks", "6 weeks")
            option.textContent = plan.name + (index === 0 ? ' (Most Popular)' : '');
            if (index === 0) option.selected = true;
            frequencySelect.appendChild(option);
          });
        }

        // Show the option
        subscribeOption.style.display = '';
        this.syncPurchaseOptionSelectionState();
      }
    } else {
      // Variant doesn't have subscription - hide the option
      if (subscribeOption) {
        subscribeOption.style.display = 'none';

        // Select one-time purchase if subscribe was selected
        const subscribeRadio = subscribeOption.querySelector('.og-radio[value="subscribe"]');
        if (subscribeRadio && subscribeRadio.checked) {
          const onetimeRadio = this.querySelector('.og-radio[value="onetime"]');
          if (onetimeRadio) {
            onetimeRadio.checked = true;

            // Remove selling plan from form
            const form = this.querySelector('.product-card-enhanced__form');
            const existingPlanInput = form?.querySelector('input[name="selling_plan"]');
            if (existingPlanInput) {
              existingPlanInput.remove();
            }

            // Update button text back to regular add to cart
            const addToCartButton = this.querySelector('.product-card-enhanced__add-to-cart');
            if (addToCartButton) {
              const buttonTextSpan = addToCartButton.querySelector('span');
              if (buttonTextSpan) {
                buttonTextSpan.textContent = 'ADD TO CART';
              }
            }
          }
        }

        this.syncPurchaseOptionSelectionState();
      }
    }
  }

  /**
   * Create subscription option dynamically
   * @param {Object} variant - Selected variant with selling plan data
   */
  createSubscriptionOption(variant) {
    if (!variant.selling_plan) return;

    const purchaseOptions = this.querySelector('.og-purchase-options');
    if (!purchaseOptions) return;

    const subPrice = (variant.selling_plan.subscription_price / 100).toFixed(2);
    const discountPct = variant.selling_plan.discount_percentage;

    // Build frequency options HTML
    let frequencyOptionsHTML = '';
    if (variant.selling_plan_options && variant.selling_plan_options.length > 0) {
      variant.selling_plan_options.forEach((plan, index) => {
        const isSelected = index === 0 ? 'selected' : '';
        const popular = index === 0 ? ' (Most Popular)' : '';
        // Use clean name from variant data (e.g., "4 weeks", "6 weeks")
        frequencyOptionsHTML += `<option value="${plan.id}" ${isSelected}>${plan.name}${popular}</option>`;
      });
    }

    const subscribeOptionHTML = `
      <div class="og-option og-option--subscribe">
        <label class="og-option-label">
          <input type="radio"
                 name="purchase-${this.productId}"
                 value="subscribe"
                 class="og-radio"
                 data-selling-plan-id="${variant.selling_plan.id}">
          <div class="og-option__content">
            <div class="og-option__header">
              <span class="og-option__label">Subscribe &amp; Save</span>
              <span class="og-badge">${discountPct}% OFF + FREE SHIPPING</span>
            </div>
            <span class="og-option__price og-option__price--subscription" data-price-display="subscription" data-base-subscription-price="${variant.selling_plan.subscription_price}">$${subPrice}</span>

            <ul class="og-benefits">
              <li class="og-benefit">
                <svg class="og-benefit__icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                <span>Get ${discountPct}% off every order</span>
              </li>
              <li class="og-benefit">
                <svg class="og-benefit__icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                <span>Free shipping on all subscription orders</span>
              </li>
              <li class="og-benefit">
                <svg class="og-benefit__icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                <span>Modify, pause, skip, or cancel anytime</span>
              </li>
            </ul>
          </div>
        </label>
        ${frequencyOptionsHTML ? `
        <div class="og-frequency-wrapper">
          <div class="og-frequency">
            <label class="og-frequency__label">Deliver Every</label>
            <select class="og-frequency-select" data-frequency-select>
              ${frequencyOptionsHTML}
            </select>
          </div>
        </div>
        ` : ''}
      </div>
    `;

    purchaseOptions.insertAdjacentHTML('beforeend', subscribeOptionHTML);

    // Re-setup event listeners for the new radio button
    const newRadio = purchaseOptions.querySelector('.og-option--subscribe .og-radio');
    if (newRadio) {
      this.setupPurchaseOptionToggle(newRadio);
    }

    // Setup frequency selector for the new dropdown
    this.setupFrequencySelector();
    this.syncPurchaseOptionSelectionState();
  }

  /**
   * Setup event listener for a single purchase option radio
   * @param {HTMLElement} radio - Radio button element
   */
  setupPurchaseOptionToggle(radio) {
    const form = this.querySelector('.product-card-enhanced__form');
    const addToCartButton = this.querySelector('.product-card-enhanced__add-to-cart');

    radio.addEventListener('change', (e) => {
      if (e.target.checked) {
        const isSubscribe = e.target.value === 'subscribe';
        const sellingPlanId = e.target.dataset.sellingPlanId;

        if (form) {
          // Remove existing selling_plan input if any
          const existingPlanInput = form.querySelector('input[name="selling_plan"]');
          if (existingPlanInput) {
            existingPlanInput.remove();
          }

          // Add selling_plan input if subscribe is selected
          if (isSubscribe && sellingPlanId) {
            const planInput = document.createElement('input');
            planInput.type = 'hidden';
            planInput.name = 'selling_plan';
            planInput.value = sellingPlanId;
            form.appendChild(planInput);
          }
        }

        // Update button text based on selection
        if (addToCartButton) {
          const buttonTextSpan = addToCartButton.querySelector('span');
          if (buttonTextSpan) {
            if (isSubscribe) {
              buttonTextSpan.textContent = 'ADD SUBSCRIPTION';
            } else {
              buttonTextSpan.textContent = 'ADD TO CART';
            }
          }
        }
      }

      this.syncPurchaseOptionSelectionState();
    });
  }

  /**
   * Update subscription price based on variant and selling plan
   * @param {Object} variant - Selected variant
   */
  updateSubscriptionPrice(variant) {
    const subscriptionPriceElement = this.querySelector('.product-card-enhanced__purchase-price--subscribe');
    if (!subscriptionPriceElement) return;

    // Get selling plan data
    const sellingPlanScript = this.querySelector('[data-selling-plan]');
    if (!sellingPlanScript) return;

    try {
      const sellingPlan = JSON.parse(sellingPlanScript.textContent);
      const priceAdjustment = sellingPlan.price_adjustment;

      let subscriptionPrice = variant.price; // Price in cents

      // Calculate subscription price based on adjustment type
      if (priceAdjustment.value_type === 'percentage') {
        const discountDecimal = priceAdjustment.value / 100;
        const discountAmount = subscriptionPrice * discountDecimal;
        subscriptionPrice = subscriptionPrice - discountAmount;
      } else if (priceAdjustment.value_type === 'fixed_amount') {
        subscriptionPrice = subscriptionPrice - priceAdjustment.value;
      } else if (priceAdjustment.value_type === 'price') {
        subscriptionPrice = priceAdjustment.value;
      }

      // Format and display
      const formattedPrice = (subscriptionPrice / 100).toFixed(2);
      subscriptionPriceElement.textContent = `$${formattedPrice}`;
      subscriptionPriceElement.dataset.subscriptionPrice = subscriptionPrice;

      // Update discount badge
      const subscribeBadge = this.querySelector('.product-card-enhanced__subscribe-badge');
      if (subscribeBadge && priceAdjustment.value_type === 'percentage') {
        const percentage = Math.round(priceAdjustment.value);
        subscribeBadge.textContent = `${percentage}% OFF`;
      }
    } catch (error) {
      console.error('Error updating subscription price:', error);
    }
  }

  /**
   * Show out of stock state - comprehensive UI updates
   */
  showOutOfStock() {
    // 1. Disable and style Add to Cart button (keep original text)
    const addToCartButton = this.querySelector('.product-card-enhanced__add-to-cart');
    if (addToCartButton) {
      addToCartButton.disabled = true;
      addToCartButton.style.backgroundColor = '#d1d5db'; // Gray
      addToCartButton.style.color = '#6b7280'; // Dark gray text
      addToCartButton.style.cursor = 'not-allowed';
      addToCartButton.style.opacity = '0.6';
      // Keep original button text - don't change to "OUT OF STOCK"
    }

    // 2. Disable and style Buy Now button (keep original text)
    const buyNowButton = this.querySelector('[data-buy-now]');
    if (buyNowButton) {
      buyNowButton.disabled = true;
      buyNowButton.style.backgroundColor = '#d1d5db'; // Gray
      buyNowButton.style.color = '#6b7280'; // Dark gray text
      buyNowButton.style.cursor = 'not-allowed';
      buyNowButton.style.opacity = '0.6';
      // Keep original button text - don't change to "OUT OF STOCK"
    }

    // 3. Disable OrderGroove purchase options widget
    const ogPurchaseOptions = this.querySelector('.og-purchase-options');
    if (ogPurchaseOptions) {
      ogPurchaseOptions.style.opacity = '0.5';
      ogPurchaseOptions.style.pointerEvents = 'none';

      // Disable OrderGroove buttons
      const ogButtons = ogPurchaseOptions.querySelectorAll('og-optout-button, og-optin-button');
      ogButtons.forEach(button => {
        button.style.pointerEvents = 'none';
      });
    }

    // 4. Show out of stock badge (below purchase options, above buttons)
    let outOfStockBadge = this.querySelector('.product-card-enhanced__out-of-stock-badge');
    if (!outOfStockBadge) {
      outOfStockBadge = document.createElement('div');
      outOfStockBadge.className = 'product-card-enhanced__out-of-stock-badge';
      outOfStockBadge.innerHTML = '<span>OUT OF STOCK</span>';

      // Insert as FIRST CHILD of actions section
      const actions = this.querySelector('.product-card-enhanced__actions');
      if (actions) {
        actions.insertBefore(outOfStockBadge, actions.firstChild);
      }
    }
    outOfStockBadge.style.display = 'block';
  }

  /**
   * Hide out of stock state - restore normal UI
   */
  hideOutOfStock() {
    // 1. Re-enable and restore Add to Cart button
    const addToCartButton = this.querySelector('.product-card-enhanced__add-to-cart');
    if (addToCartButton) {
      addToCartButton.disabled = false;
      addToCartButton.style.backgroundColor = ''; // Reset to CSS default
      addToCartButton.style.color = '';
      addToCartButton.style.cursor = '';
      addToCartButton.style.opacity = '';
      // Don't change button text - it already says "ADD TO CART"
    }

    // 2. Re-enable and restore Buy Now button
    const buyNowButton = this.querySelector('[data-buy-now]');
    if (buyNowButton) {
      buyNowButton.disabled = false;
      buyNowButton.style.backgroundColor = ''; // Reset to CSS default
      buyNowButton.style.color = '';
      buyNowButton.style.cursor = '';
      buyNowButton.style.opacity = '';
      // Don't change button text - it already says "BUY NOW"
    }

    // 3. Re-enable OrderGroove purchase options widget
    const ogPurchaseOptions = this.querySelector('.og-purchase-options');
    if (ogPurchaseOptions) {
      ogPurchaseOptions.style.opacity = '';
      ogPurchaseOptions.style.pointerEvents = '';

      // Re-enable OrderGroove buttons
      const ogButtons = ogPurchaseOptions.querySelectorAll('og-optout-button, og-optin-button');
      ogButtons.forEach(button => {
        button.style.pointerEvents = '';
      });
    }

    // 4. Hide out of stock badge
    const outOfStockBadge = this.querySelector('.product-card-enhanced__out-of-stock-badge');
    if (outOfStockBadge) {
      outOfStockBadge.style.display = 'none';
    }
  }

  /**
   * Setup color swatch interactions
   * Handles swatch clicks to change variant and update product image
   */
  setupColorSwatches() {
    const swatchLists = this.querySelectorAll('.product-card-enhanced__swatch-list, .product-card-enhanced__variant-list');

    if (!swatchLists.length) return;

    swatchLists.forEach(swatchList => {
      const swatchInputs = swatchList.querySelectorAll('[type="radio"]');
      const optionPosition = swatchList.dataset.optionPosition;

      // Store original image once when first setting up swatches
      this._storeOriginalImage();

      // Attach change listeners to individual inputs
      swatchInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (input.checked) {
            // On actual selection, update the variant (which updates the image permanently)
            this._onSwatchChanged(input, optionPosition);
            // Update stored original to the newly selected variant's image
            this._updateOriginalImageAfterSelection();
          }
        });
      });

      // Image changes only on selection (via change listener above), not on hover
    });
  }

  /**
   * Handle swatch selection change
   * Updates the corresponding variant dropdown which triggers the full update flow
   */
  _onSwatchChanged(input, optionPosition) {
    const selectedValue = input.value;
    const variantSelect = this.querySelector(`select.product-card-enhanced__variant-select[data-option-position="${optionPosition}"]`);

    if (variantSelect) {
      variantSelect.value = selectedValue;

      // If this is the primary option, filter secondary options first
      if (this.primarySelectPosition && parseInt(optionPosition) === this.primarySelectPosition) {
        this.filterSecondaryOptions();
      }

      // Update the selected variant with all current selections
      const variantSelects = this.querySelectorAll('.product-card-enhanced__variant-select');
      if (this.variants) {
        this.updateSelectedVariant(this.variants, variantSelects);
      }
    }
  }

  /**
   * Handle swatch hover for preview
   * Shows the variant image on hover without changing the selection
   */
  _onSwatchHovered(input) {
    const variantMedia = input.dataset.variantMedia;
    if (!variantMedia) return;

    try {
      const media = JSON.parse(variantMedia);
      this._showPreviewImage(media);
    } catch (e) {
      // Silently fail if media data is invalid
    }
  }

  /**
   * Store the current selected variant's image as the original
   */
  _storeOriginalImage() {
    const firstSlide = this.querySelector('[data-carousel-slide]');
    if (!firstSlide) return;

    const imageElement = firstSlide.querySelector('.product-card-enhanced__image');
    if (!imageElement) return;

    const currentSrc = imageElement.src;
    // Only store if it's a valid image URL (contains cdn/shop or products)
    if (currentSrc && (currentSrc.includes('/cdn/shop/') || currentSrc.includes('/products/'))) {
      this._originalImageSrc = currentSrc;
    }
  }

  /**
   * Update the stored original image after a swatch selection
   */
  _updateOriginalImageAfterSelection() {
    // Wait a moment for the variant update to complete
    setTimeout(() => {
      this._storeOriginalImage();
    }, 100);
  }

  /**
   * Show preview image on hover
   */
  _showPreviewImage(media) {
    const firstSlide = this.querySelector('[data-carousel-slide]');
    if (!firstSlide) return;

    const imageElement = firstSlide.querySelector('.product-card-enhanced__image');
    if (!imageElement || !media || !media.src) return;

    // Update both src AND srcset for responsive images
    imageElement.src = media.src;
    imageElement.srcset = this._generateSrcset(media.src);

    if (media.alt) imageElement.alt = media.alt;
  }

  /**
   * Restore to the currently selected variant's image
   */
  _restoreToSelectedVariant() {
    if (!this._originalImageSrc) return;

    const firstSlide = this.querySelector('[data-carousel-slide]');
    if (!firstSlide) return;

    const imageElement = firstSlide.querySelector('.product-card-enhanced__image');
    if (!imageElement) return;

    // Check if we're already showing the correct image
    const currentNormalized = this._normalizeUrl(imageElement.src);
    const originalNormalized = this._normalizeUrl(this._originalImageSrc);

    if (currentNormalized === originalNormalized) return;

    // Restore both src AND srcset
    imageElement.src = this._originalImageSrc;
    imageElement.srcset = this._generateSrcset(this._originalImageSrc);
  }

}

// Register the custom element
if (!customElements.get('product-card-enhanced')) {
  customElements.define('product-card-enhanced', ProductCardEnhanced);
}

/**
 * Initialize all enhanced product cards on page load
 */
document.addEventListener('DOMContentLoaded', () => {
  const cards = document.querySelectorAll('product-card-enhanced');
  cards.forEach(card => {
    // Cards are automatically initialized via the constructor
    // This is just for any additional setup if needed
  });
});
